'use client';

import { Share } from 'lucide-react';

import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';
import { useShare } from '@/hooks/useShare';
import type { ShareEntityDescriptor, ShareEntityType } from '@/lib/share/types';

/**
 * WEB-601 — the mobile/tablet share icon button (DS 1.11.0 · DES-103).
 *
 * One icon-only `Button` (`text` · `sm` · `icon`) that calls
 * `useShare(descriptor).share()`. It trails the breadcrumb row on the Event and
 * Venue detail pages. Paint, hover/pressed/focus ink and the 44px coarse-pointer
 * hit box come from `.m-share` + the button atom (`styles/components`); it is
 * hidden at >= 1024px by that CSS alone, so there is no `matchMedia` or
 * `navigator.share` gating and SSR and the client always agree.
 */
export interface ShareButtonProps {
  /** The entity to share. Serializable, so a Server Component can build it. */
  descriptor: ShareEntityDescriptor;
  className?: string;
}

const ARIA_LABELS: Record<ShareEntityType, string> = {
  event: 'Share event',
  venue: 'Share venue',
  artist: 'Share artist',
  city: 'Share city',
};

export function ShareButton({ descriptor, className }: ShareButtonProps): React.ReactElement {
  const { share } = useShare(descriptor);

  return (
    <Button
      type="button"
      variant="text"
      size="sm"
      icon
      className={cn('m-share', className)}
      aria-label={ARIA_LABELS[descriptor.type]}
      onClick={() => {
        void share();
      }}
    >
      <Share aria-hidden="true" focusable="false" size={18} strokeWidth={2} />
    </Button>
  );
}
